import React from 'react'
import {
  makeStyles,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core'
import gamesListStyles from './gameList.styles'
import useHistoryPush from '../../hooks/useHistoryPush'
import { format } from 'date-fns'
import QueryKeys from '../../utils/constants/queryKeys'
import { useQuery } from 'react-query'
import useQueryParams from '../../hooks/useQueryParams'
import { useLocation, useParams } from 'react-router-dom'
import COLORS from '../../utils/constants/colors'
import getTeamGamesStats from '../../Apis/stats/getTeamGamesStats'
import getSeasonTeamGamesStats from '../../Apis/stats/getSeasonTeamGamesStats'
import ContainerPadding from '../../components/containerPadding/containerPadding'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import GameScore from './GameScore'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import STATS_GAME_STATUS from '../../utils/constants/statsGameStatus'
import NoGamesAvalaiblePage from './NoGamesAvalaiblePage'

const GamesList = ({ orgId, resolvedTeamId, resolvedSeasonId }) => {
  const useStyles = makeStyles(gamesListStyles)
  const classes = useStyles()
  const { redirect } = useHistoryPush()
  const queryParams = useQueryParams()
  const location = useLocation()
  const { teamId } = useParams()

  // Determine if we're on the season teams route
  const isSeasonTeamRoute = location.pathname.includes('/seasonTeams')

  // Use teamId from URL params if available, otherwise fall back to query params
  const activeTeamId = teamId || queryParams?.team
  // A team page's team is one of the selected org's (?org), even when the stats browse a paired
  // federation (orgId): its game log is read under the team's own org.
  const teamOrgId = queryParams?.org

  const { data: games, isFetching } = useQuery(
    [QueryKeys.GET_TEAM_GAMES_STATS, isSeasonTeamRoute ? orgId : teamOrgId, isSeasonTeamRoute ? resolvedTeamId : activeTeamId],
    () => {
      if (isSeasonTeamRoute) {
        return getSeasonTeamGamesStats({ orgId, teamId: resolvedTeamId, seasonId: resolvedSeasonId })
      }
      return getTeamGamesStats({ orgId: teamOrgId, teamId: activeTeamId })
    },
    {
      enabled: Boolean(activeTeamId),
    },
  )

  const handleGameClick = (eventId) => {
    redirect(`/game/stats`, [{ eventId }])
  }

  if (!games?.data || games?.data.length === 0) {
    return <NoGamesAvalaiblePage />
  }

  return (
    <ContainerPadding paddingValue='0%'>
      {isFetching ? (
        <ContainerPadding paddingValue='45%'>
          <LoaderDots
            style={{
              fill: COLORS.orange,
              width: 70,
              height: 70,
            }}
          />
        </ContainerPadding>
      ) : (
        <Paper className={classes.root}>
          <TableContainer>
            <Table>
              <TableBody>
                {games?.data.map((game) => {
                  const isLive = game.status === STATS_GAME_STATUS.LIVE
                  const formattedDate = format(new Date(game.startDateTime), 'dd MMM yyyy, HH:mm')

                  const firstTeam = isSeasonTeamRoute
                    ? {
                        teamName: game.homeTeam.teamName,
                        teamLogo: game.homeTeam.teamLogoUrl,
                        teamScore: game.homeTeam.score,
                      }
                    : game.team1
                  const secondTeam = isSeasonTeamRoute
                    ? {
                        teamName: game.awayTeam.teamName,
                        teamLogo: game.awayTeam.teamLogoUrl,
                        teamScore: game.awayTeam.score,
                      }
                    : game.team2
                  const subLabel = isSeasonTeamRoute ? game.stageName : game.competitionName

                  return (
                    <TableRow
                      key={game.gameEventId}
                      className={classes.game}
                      onClick={() => handleGameClick(game.gameEventId)}
                    >
                      <TableCell style={{ width: '15%' }}>
                        <Typography className={classes.gameDate} variant='body2'>
                          {formattedDate}
                          {subLabel && (
                            <Typography className={classes.notes} variant='body2'>
                              {subLabel}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ width: '75%' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <GameScore
                            firstTeam={firstTeam}
                            secondTeam={secondTeam}
                            place={game.place}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        style={{
                          width: '10%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 7,
                          }}
                        >
                          {isLive && (
                            <>
                              <FiberManualRecordIcon
                                style={{
                                  fill: COLORS.errorRed,
                                  fontSize: '24px',
                                }}
                                className={classes.liveBanner}
                              />
                              <Typography className={classes.liveBanner} variant='body2'>
                                {game.status}
                              </Typography>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </ContainerPadding>
  )
}

export default GamesList
